/***********************************************************************************************
 * 영상 모음 화면
 *
 * CopyRight (c) 2023 박주형 All rights reserved.
 * swiper@9 붙이는데 참고한 소스: https://stackoverflow.com/questions/75326426/how-to-use-swiper-9-with-angular
 **********************************************************************************************/
import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {Subscription, timer} from "rxjs";
import {finalize} from "rxjs/operators";
import Swiper, {A11y, Navigation, Pagination, SwiperOptions} from "swiper";

import {VIDEO_CATEGORY} from "../shared/defines/protocol";
import {Video} from "../shared/models/video.model";
import {
  BookmarkChange,
  findVideoData,
  HISTORY_KEY_SEARCH_VIDEO,
  HISTORY_KEY_VIEW_VIDEO,
  LIST_TYPE,
  SearchParam
} from "../shared/defines/client.const";
import {MENU_PAGE} from '../shared/defines/desktop.const';
import {VideoLoader} from "../shared/interfaces/VideoLoader";
import {VideoService} from "../shared/services/video/video.service";
import {VideoBookmarkService} from "../shared/services/video/video-bookmark.service";
import {VideoHistoryService} from "../shared/services/video/video-history.service";
import {GlobalEventService} from "../shared/services/global-event/global-event.service";
import {LayoutMgrService} from "../shared/services/layout-mgr/layout-mgr.service";


const SWIPER_CONFIG: SwiperOptions = {
  modules: [A11y],
  slidesPerView: 4,
  slidesPerGroup: 4,
  spaceBetween: 2,
  // navigation: true,
  pagination: {
    el: '.swiper-pagination',
    type: 'bullets',
  },
  // autoplay: true,
  // observeParents: true,
  // loop: true,
};


type VIDEO_COLLECT = {
  category: VIDEO_CATEGORY;
  videoCollect: Array<Video>;
};

class VideoCollect {
  
  /**
   * 영상 범주 목록
   * TODO: 영상 범주가 급격히 바뀐다면, 범주 정보도 서버에서 받아오도록 수정할 필요가 있다.
   */
  protected listVideoCollect: Array<VIDEO_COLLECT> = [
    {
      category: VIDEO_CATEGORY.DIALECT,
      videoCollect: new Array<Video>(),
    },
    {
      category: VIDEO_CATEGORY.COOL_EXPR,
      videoCollect: new Array<Video>(),
    },
    {
      category: VIDEO_CATEGORY.WRONG_EXPR,
      videoCollect: new Array<Video>(),
    },
    {
      category: VIDEO_CATEGORY.PROVERB,
      videoCollect: new Array<Video>(),
    },
    {
      category: VIDEO_CATEGORY.LOAN_WORD,
      videoCollect: new Array<Video>(),
    },
    {
      category: VIDEO_CATEGORY.HOMONYM,
      videoCollect: new Array<Video>(),
    },
  ];

  public get ListCategory(): Array<VIDEO_COLLECT> {
    return this.listVideoCollect.filter(cat => cat.videoCollect.length > 0);
  }

  public FindVideoCollect(category: VIDEO_CATEGORY): VIDEO_COLLECT | null {
    return this.listVideoCollect.find(item => item.category === category) ?? null;
  }

  public Clear(): void {
    this.listVideoCollect.forEach(item => item.videoCollect = new Array<Video>());
  }
}

/**
 * 카테고리 초기화 딜레이 (ms)
 * API 로 카테고리 수신 후 rendering 완료까지의 대기 시간.
 * 대기 후 Swiper 초기화 진행.
 */
const CAT_INIT_DELAY = 200;


@Component({
  selector: 'app-movie-collect',
  templateUrl: './movie-collect.component.html',
  styleUrls: ['./movie-collect.component.scss']
})
export class MovieCollectComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('container') container?: ElementRef; // ngAfterViewInit() 에서 사용 가능
  @ViewChild('main', { static: true }) main?: ElementRef; // ngOnInit() 에서 사용 가능

  protected readonly findVideoData = findVideoData;
  protected readonly MENU_PAGE = MENU_PAGE;
  protected readonly HISTORY_KEY_SEARCH_VIDEO = HISTORY_KEY_SEARCH_VIDEO;
  protected readonly HISTORY_KEY_VIEW_VIDEO = HISTORY_KEY_VIEW_VIDEO;
  protected readonly VIDEO_CATEGORY = VIDEO_CATEGORY;

  // 헤더에 넣는 값
  public selectedMenu: MENU_PAGE = MENU_PAGE.MOVIE_COLLECT;
  protected keyword: string | null = null;
  protected placeholder: string | null = null;
  public emptyMsg: string | null = null;
  protected scrollTop = true;

  // 구독 관리를 위한 Subscription 변수들
  private queryParams$?: Subscription;
  private params$?: Subscription;
  private load$: Subscription | null = null;

  public get IsLoading(): boolean {
    return this.load$ !== null;
  }

  public get IsComplete(): boolean {
    return !this.IsLoading && this.listVideo !== null;
  }

  private sendingBookmark = false;

  private type: LIST_TYPE | null = null;
  public vl: VideoLoader | null = null;
  protected listVideo: Array<Video> | null = null;
  protected readonly videoCollect = new VideoCollect();

  // 라우터에서 넣어주는 값. (TODO: 초기화에 유의해야 함)
  private queryParams: SearchParam | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private lm: LayoutMgrService,
    private videoSvc: VideoService,
    private bmSvc: VideoBookmarkService,
    private historySvc: VideoHistoryService,
    private globalEventSvc: GlobalEventService,
  ) {
    // super(globalEventSvc);

    /******************************************************************************************
     * 라우터 파라미터 변경 핸들러 - 주소 뒤의 파라미터를 받아오는 곳. ( ?keyword=... )
     * 입력된 파라미터가 없어도 처음 초기화 할 때에도 불려진다.
     * 처음 화면에 들어올 때, 라우터 주소 변견 핸들러보다 더 먼저 불려진다. (이상하지만 그렇다)
     *
     * router.navigate()로 같은 주소를 호출하면 이 코드가 다시 불려진다.
     */
    this.queryParams$ = this.route.queryParams
      .subscribe(params => {
        console.log('MovieCollectComponent::queryParams', params);

        this.queryParams = {...params};
        delete this.queryParams.serial;

        if (this.type) {
          // 라우터 주소 변경 핸들러로 들어와서 init()이 처음 불려진 경우 = 이미 화면이 목록이 불려진 경우 = 목록이 초기화되어있는 경우!
          // 이 조건을 안 걸면 init()가 두번 불려진다.

          // TODO: 현재 목록에 존재하면 해당 영상의 위치로 이동, 없다면 이력 화면으로 이동시킴.
          this.init();
        }
      });

    /******************************************************************************************
     * 라우터 주소 변경 핸들러 - 라우터 주소 읽어오는 곳.
     * 라우터 주소에 포함된 파라미터가 변경되어도 불려진다. (여기서는 type만 변경되어도 불려짐)
     *
     * queryParams가 바뀐 것(주소뒤의 파리미터가 변경된 경우)으로는 여기가 불려지지 않는다.
     */
    this.params$ = route.params.subscribe(params => {
      console.log('MovieCollectComponent params =', params);
      this.type = params['type'] ?? null;

      this.init();
    });
  }


  ngAfterViewInit(): void {
  }

  ngOnInit() {
    // if (this.main) {
    //   this.InitFocusMain(this.main);
    // }
    // else {
    //   console.error('MovieCollectComponent::ngOnInit() main is null');
    // }
  }

  ngOnDestroy() {
    // YouTube 플레이어 팝업이 열려있으면 닫기
    this.lm.CloseYoutubePopup();

    // ActivatedRoute 관련 구독 해지 처리
    this.queryParams$?.unsubscribe();
    this.params$?.unsubscribe();
    // 리스트 관련 구독 해지 처리 (진행중이면)
    this.load$?.unsubscribe();
    // this.ExitFocusMain();
  }

  protected onGoHistory(): void {
    console.log('MovieCollectComponent::onGoHistory');

    this.router.navigate(['/movie-collect', LIST_TYPE.HISTORY]).then();
  }

  protected SearchVideo(param: SearchParam): void {
    console.log('MovieCollectComponent::SearchVideo', param);

    if (param?.keyword) {
      this.router.navigate(['/movie-collect', LIST_TYPE.SERVER ], {
        queryParams: {keyword: param.keyword}
      }).then();
    }
  }

  private init() {
    console.log('MovieCollectComponent::init', this.type, this.queryParams);

    let param: SearchParam | undefined;
    if (this.queryParams?.id) {
      param = {id: this.queryParams.id};
    }
    else if (this.queryParams?.keyword) {
      this.keyword = this.queryParams.keyword;
      param = {keyword: this.keyword};
    }

    switch (this.type) {
      case LIST_TYPE.SERVER:
        this.selectedMenu = MENU_PAGE.MOVIE_COLLECT;
        this.vl = this.videoSvc;
        this.placeholder = '영상을 검색 합니다.';
        this.emptyMsg = '영상이 없습니다.';
        this.keyword = this.queryParams?.keyword ?? null;
        this.ReloadList(param);
        break;
      case LIST_TYPE.BOOKMARK:
        this.selectedMenu = MENU_PAGE.MOVIE_BOOKMARK;
        this.vl = this.bmSvc;
        this.placeholder = '영상을 검색 합니다.';
        this.emptyMsg = '영상이 없습니다.';
        this.keyword = this.queryParams?.keyword ?? null;
        this.ReloadList(param);
        break;
      case LIST_TYPE.HISTORY:
        this.selectedMenu = MENU_PAGE.MOVIE_HISTORY;
        this.vl = this.historySvc;
        this.placeholder = '영상을 검색 합니다.';
        this.emptyMsg = '영상이 없습니다.';
        this.ReloadList();
        break;
      default:
        console.error('MovieCollectComponent invalid type', this.type);
        break;
    }
  }

  private ReloadList(param?: SearchParam): void {
    console.log('MovieCollectComponent::ReloadList', param, this.queryParams);
    this.listVideo = null;
    this.videoCollect.Clear();

    if (this.vl === null) {
      throw new Error('MovieCollectComponent::ReloadList vl is null');
    }

    this.load$ = this.vl.GetList(param).pipe(
      finalize(() => this.load$ = null)
    ).subscribe(
      (listVideo: Array<Video>) => {
        console.log('listVideo = ', listVideo);

        this.listVideo = listVideo;

        listVideo.forEach(video => {
          const collect = this.videoCollect.FindVideoCollect(video.category);
          if (collect) {
            collect.videoCollect.push(video);
          }
        });

        console.log('videoCollect = ', this.videoCollect);

        timer(CAT_INIT_DELAY).subscribe(() => {
          this.initSwiper();
        });
      },
      err => console.error(err));
  }

  /**
   * Swiper 초기화
   * @private
   */
  private initSwiper() {
    this.videoCollect.ListCategory.forEach((item: VIDEO_COLLECT, idx: number) => {
      const config = {
        ...SWIPER_CONFIG,
        navigation: {
          disabledClass: 'swiper-button-disabled',
          prevEl: `.cat-${idx} .custom-button-prev`, // 이전 버튼 클래스명
          nextEl: `.cat-${idx} .custom-button-next`, // 다음 버튼 클래스명
        },
        on: {
          init: (swiper: Swiper) => {
            console.log('swiper init', idx, swiper, item, config);
          }
        }
      };

      // @ts-ignore
      // this.checkSelector(config.navigation?.nextEl);
      // @ts-ignore
      // this.checkSelector(config.navigation?.prevEl);

      const selector = `.cat-${idx} .swiper`;
      if (document.querySelector(selector)) {

        const swiper = new Swiper(selector, config);
        // console.warn(`swiper[${selector}] found`, idx, swiper, item, config);
      } else {
        console.error(`swiper[${selector}] not found`, idx);
      }
    });
  }

  /**
   * 선택자가 실제로 존재하는지 확인하는 함수
   * @param selector
   * @private
   */
  private checkSelector(selector: string): void {
    const el = document.querySelector(selector)
    if (el) {
      console.warn(`selector[${selector}] found`, el);
    } else {
      console.error(`selector[${selector}] not found`);
    }
  }

  /****************************************************************************
   * 스크롤 처리
   ****************************************************************************/
  protected onScrollTop($event: Event): void {
    const target = $event.target as HTMLElement;

    // console.log('onScrollTop() ', target.scrollTop);
    this.scrollTop = target.scrollTop === 0; // 수직 스크롤 위치
  }

  protected onClickRope($event: MouseEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    this.container?.nativeElement.scrollTo({
      top: 0,
      behavior: 'smooth',
      // behavior: 'auto',
    });
  }

  /****************************************************************************
   * 책갈피 실시간 처리
   ****************************************************************************/
  protected onBookmarkChange(bookmarkChange: BookmarkChange, listVideo: Array<Video>): void {
    if (this.type === LIST_TYPE.BOOKMARK) {
      console.log('MovieCollectComponent::onBookmarkChange() bookmarkChange =', bookmarkChange, listVideo);

      if (bookmarkChange.bookmark) {
        // 추가는 하지 않는다.
      }
      else {
        // 책갈피 삭제
        const idx = listVideo.findIndex(video => video.no === bookmarkChange.id);
        if (idx >= 0) {
          listVideo.splice(idx, 1);
        }

        const idx2 = this.listVideo?.findIndex(video => video.no === bookmarkChange.id);
        if (idx2 !== undefined && idx2 >= 0) {
          this.listVideo?.splice(idx2, 1);
        }
      }
    }
  }

  onClickSeeMore(category: VIDEO_CATEGORY) {
    // 더 보기 버튼 클릭 시 처리할 로직
    console.log('더 보기 클릭:', category);
    if (category === VIDEO_CATEGORY.DIALECT) {
      this.router.navigate(['/dialect/region']);
    }
    else {
      console.error('MovieCollectComponent::onClickSeeMore() invalid category', category);
    }
  }

  /**
   * 해당 카테고리에 대해 [더 보기] 버튼을 표시할지 판단하는 메서드
   * DIALECT 영상의 경우 영상이 2개를 초과할 때에만 더 보기 버튼을 표시한다.
   * @param cat VIDEO_COLLECT 객체
   */
  protected isShowMoreButton(cat: VIDEO_COLLECT): boolean {
    return cat.category === this.VIDEO_CATEGORY.DIALECT && cat.videoCollect.length > 2;
  }
}
